import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Key,
  Code2,
  Zap,
  Copy,
  Check,
  ChevronRight,
  ExternalLink,
  Terminal,
  FileCode,
} from 'lucide-react';
import { cn } from '@/client/lib/utils';

type Section = 'getting-started' | 'authentication' | 'quick-start' | 'api-reference' | 'examples';

interface NavItem {
  id: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'authentication', label: 'Authentication', icon: Key },
  { id: 'quick-start', label: 'Quick Start', icon: Zap },
  { id: 'api-reference', label: 'API Reference', icon: FileCode },
  { id: 'examples', label: 'Code Examples', icon: Code2 },
];

function CodeBlock({ code, language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <button
        onClick={copyCode}
        className="absolute top-2 right-2 p-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy code"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function SectionGettingStarted() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Getting Started</h2>
        <p className="text-muted-foreground">
          Welcome to the Armenian Link API. This guide will help you get started with programmatic
          access to our URL shortening service.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">1. Create an Account</h3>
        <p className="text-muted-foreground">
          First, sign up for an account at{' '}
          <Link to="/login" className="text-primary hover:underline">
            armenian.link/login
          </Link>
          . You&apos;ll need an account to generate API tokens.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">2. Generate an API Token</h3>
        <p className="text-muted-foreground">
          Navigate to{' '}
          <Link to="/settings/tokens" className="text-primary hover:underline">
            Settings → API Tokens
          </Link>{' '}
          and create a new token. Select the scopes you need:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
          <li>
            <code className="text-sm bg-muted px-1 rounded">links:read</code> - Read your links and
            analytics
          </li>
          <li>
            <code className="text-sm bg-muted px-1 rounded">links:write</code> - Create new short
            links
          </li>
          <li>
            <code className="text-sm bg-muted px-1 rounded">links:delete</code> - Delete your links
          </li>
        </ul>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">3. Make Your First Request</h3>
        <p className="text-muted-foreground">
          Use your token to authenticate API requests. See the Quick Start section for examples.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm text-amber-800">
          <strong>Important:</strong> Keep your API token secure. Never share it publicly or commit
          it to version control. If compromised, delete the token immediately from your settings.
        </p>
      </div>
    </div>
  );
}

function SectionAuthentication() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Authentication</h2>
        <p className="text-muted-foreground">
          The Armenian Link API uses Bearer token authentication for all authenticated endpoints.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Bearer Token</h3>
        <p className="text-muted-foreground">
          Include your API token in the{' '}
          <code className="text-sm bg-muted px-1 rounded">Authorization</code> header:
        </p>
        <CodeBlock code={`Authorization: Bearer arml_your_api_token_here`} />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Token Format</h3>
        <p className="text-muted-foreground">
          API tokens start with the prefix{' '}
          <code className="text-sm bg-muted px-1 rounded">arml_</code> followed by 64 hexadecimal
          characters. Example:
        </p>
        <CodeBlock code={`arml_a1b2c3d4e5f6…`} />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Rate Limits</h3>
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Authenticated requests</span>
            <span className="font-mono text-sm">100 requests/minute</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Public endpoints</span>
            <span className="font-mono text-sm">10 requests/minute</span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Rate limit information is included in response headers:
        </p>
        <CodeBlock
          code={`X-RateLimit-Limit: 100
X-RateLimit-Remaining: 99
X-RateLimit-Reset: 1705320000`}
        />
      </div>
    </div>
  );
}

function SectionQuickStart() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Quick Start</h2>
        <p className="text-muted-foreground">Get up and running with the API in minutes.</p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Create Your First Short Link</h3>
        <p className="text-muted-foreground">
          Use the <code className="text-sm bg-muted px-1 rounded">POST /api/v1/shorten</code>{' '}
          endpoint:
        </p>
        <CodeBlock
          language="bash"
          code={`curl -X POST https://armenian.link/api/v1/shorten \\
  -H "Authorization: Bearer arml_your_token" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com/my-long-url", "title": "My Link"}'`}
        />
        <p className="text-muted-foreground">Response:</p>
        <CodeBlock
          language="json"
          code={`{
  "shortCode": "abc123",
  "shortUrl": "https://arml.ink/abc123",
  "longUrl": "https://example.com/my-long-url",
  "title": "My Link",
  "createdAt": "2025-01-15T10:30:00.000Z"
}`}
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Get Link Statistics</h3>
        <p className="text-muted-foreground">Retrieve details and click count for a link:</p>
        <CodeBlock
          language="bash"
          code={`curl https://armenian.link/api/v1/links/abc123 \\
  -H "Authorization: Bearer arml_your_token"`}
        />
        <p className="text-muted-foreground">Response:</p>
        <CodeBlock
          language="json"
          code={`{
  "shortCode": "abc123",
  "shortUrl": "https://arml.ink/abc123",
  "longUrl": "https://example.com/my-long-url",
  "title": "My Link",
  "clicks": 150,
  "isActive": true,
  "createdAt": "2025-01-15T10:30:00.000Z"
}`}
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Public API (No Auth)</h3>
        <p className="text-muted-foreground">
          Create temporary links without authentication (expire after 90 days):
        </p>
        <CodeBlock
          language="bash"
          code={`curl -X POST https://armenian.link/api/public/shorten \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com/page"}'`}
        />
      </div>
    </div>
  );
}

function SectionApiReference() {
  const endpoints = [
    {
      method: 'POST',
      path: '/api/v1/shorten',
      description: 'Create a new short URL',
      scope: 'links:write',
    },
    {
      method: 'GET',
      path: '/api/v1/links',
      description: 'List all your links (paginated)',
      scope: 'links:read',
    },
    {
      method: 'GET',
      path: '/api/v1/links/:shortCode',
      description: 'Get link details and statistics',
      scope: 'links:read',
    },
    {
      method: 'DELETE',
      path: '/api/v1/links/:shortCode',
      description: 'Delete a link permanently',
      scope: 'links:delete',
    },
    {
      method: 'POST',
      path: '/api/public/shorten',
      description: 'Create a public link (no auth, 90-day expiry)',
      scope: 'none',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">API Reference</h2>
        <p className="text-muted-foreground">
          Complete reference for all available endpoints. For interactive documentation, visit our{' '}
          <a
            href="https://armenian.link/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            Swagger UI <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Base URLs</h3>
        <div className="rounded-lg border p-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">API</span>
            <code className="text-sm bg-muted px-2 py-1 rounded">https://armenian.link/api</code>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Short URLs</span>
            <code className="text-sm bg-muted px-2 py-1 rounded">https://arml.ink</code>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Endpoints</h3>
        <div className="space-y-3">
          {endpoints.map((endpoint) => (
            <div key={endpoint.path} className="rounded-lg border p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'px-2 py-1 rounded text-xs font-bold',
                      endpoint.method === 'GET' && 'bg-green-100 text-green-700',
                      endpoint.method === 'POST' && 'bg-blue-100 text-blue-700',
                      endpoint.method === 'DELETE' && 'bg-red-100 text-red-700'
                    )}
                  >
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono">{endpoint.path}</code>
                </div>
                {endpoint.scope !== 'none' && (
                  <span className="text-xs bg-muted px-2 py-1 rounded">{endpoint.scope}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">{endpoint.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> For detailed request/response schemas and to try the API
          interactively, use our{' '}
          <a
            href="https://armenian.link/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Swagger documentation
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function SectionExamples() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Code Examples</h2>
        <p className="text-muted-foreground">
          Ready-to-use examples in popular programming languages.
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            cURL
          </h3>
          <CodeBlock
            language="bash"
            code={`# Create a short link
curl -X POST https://armenian.link/api/v1/shorten \\
  -H "Authorization: Bearer $API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com/page", "title": "My Link"}'

# List all links
curl https://armenian.link/api/v1/links \\
  -H "Authorization: Bearer $API_TOKEN"

# Get link details
curl https://armenian.link/api/v1/links/abc123 \\
  -H "Authorization: Bearer $API_TOKEN"

# Delete a link
curl -X DELETE https://armenian.link/api/v1/links/abc123 \\
  -H "Authorization: Bearer $API_TOKEN"`}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            JavaScript / Node.js
          </h3>
          <CodeBlock
            language="javascript"
            code={`const API_TOKEN = 'arml_your_token_here';
const API_URL = 'https://armenian.link/api';

// Create a short link
async function createLink(url, title) {
  const response = await fetch(\`\${API_URL}/v1/shorten\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${API_TOKEN}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ url, title })
  });
  return response.json();
}

// List all links
async function listLinks(page = 1, limit = 20) {
  const response = await fetch(
    \`\${API_URL}/v1/links?page=\${page}&limit=\${limit}\`,
    { headers: { 'Authorization': \`Bearer \${API_TOKEN}\` } }
  );
  return response.json();
}

// Get link details
async function getLink(shortCode) {
  const response = await fetch(
    \`\${API_URL}/v1/links/\${shortCode}\`,
    { headers: { 'Authorization': \`Bearer \${API_TOKEN}\` } }
  );
  return response.json();
}

// Example usage
const link = await createLink('https://example.com', 'Example');
console.log(link.shortUrl); // https://arml.ink/abc123`}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Python
          </h3>
          <CodeBlock
            language="python"
            code={`import requests

API_TOKEN = 'arml_your_token_here'
API_URL = 'https://armenian.link/api'
HEADERS = {'Authorization': f'Bearer {API_TOKEN}'}

def create_link(url: str, title: str = None) -> dict:
    """Create a new short link."""
    response = requests.post(
        f'{API_URL}/v1/shorten',
        headers=HEADERS,
        json={'url': url, 'title': title}
    )
    return response.json()

def list_links(page: int = 1, limit: int = 20) -> dict:
    """List all links with pagination."""
    response = requests.get(
        f'{API_URL}/v1/links',
        headers=HEADERS,
        params={'page': page, 'limit': limit}
    )
    return response.json()

def get_link(short_code: str) -> dict:
    """Get details for a specific link."""
    response = requests.get(
        f'{API_URL}/v1/links/{short_code}',
        headers=HEADERS
    )
    return response.json()

# Example usage
link = create_link('https://example.com', 'Example')
print(f"Short URL: {link['shortUrl']}")
print(f"Clicks: {link.get('clicks', 0)}")`}
          />
        </div>
      </div>

      <div className="rounded-lg border p-4 mt-8">
        <h3 className="font-semibold mb-2">SDKs Coming Soon</h3>
        <p className="text-sm text-muted-foreground">
          We&apos;re working on official SDKs for JavaScript/TypeScript, Python, and Go. Stay tuned
          for updates!
        </p>
      </div>
    </div>
  );
}

export function DocsPage() {
  const [activeSection, setActiveSection] = useState<Section>('getting-started');

  const renderSection = () => {
    switch (activeSection) {
      case 'getting-started':
        return <SectionGettingStarted />;
      case 'authentication':
        return <SectionAuthentication />;
      case 'quick-start':
        return <SectionQuickStart />;
      case 'api-reference':
        return <SectionApiReference />;
      case 'examples':
        return <SectionExamples />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl font-bold">
              Armenian Link
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground">API Documentation</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://armenian.link/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <Terminal className="h-4 w-4" />
              Swagger UI
            </a>
            <Link
              to="/login"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <nav className="w-64 flex-shrink-0">
            <div className="sticky top-8 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left',
                    activeSection === item.id
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {activeSection === item.id && <ChevronRight className="h-4 w-4 ml-auto" />}
                </button>
              ))}
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 min-w-0 max-w-3xl">{renderSection()}</main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t mt-16 py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Armenian Link API Documentation</p>
          <p className="mt-1">
            Questions? Contact us at{' '}
            <a href="mailto:hello@armenian.link" className="text-primary hover:underline">
              hello@armenian.link
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
